import { Role } from 'auth';
import { useGetUserInfo } from 'auth';
import {
  ContractFileCategory,
  ContractPermissionLevel,
  ContractStatus,
} from 'compliance-api/generated';
import { FormikProvider, useFormik, useFormikContext } from 'formik';
import { get } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { useClientsListQuery } from '@/modules/common/queries/clients.ts';
import { ContractByIDHeaderAction } from '@/modules/admin-dashboard/components/contractManagement/ContractByID/ContractByIDHeaderAction.tsx';
import { ContractByIDPreviewPanel } from '@/modules/admin-dashboard/components/contractManagement/ContractByID/ContractByIDPreviewPanel.tsx';
import {
  ContractByIDRightPanel,
  type ContractInfoTab,
} from '@/modules/admin-dashboard/components/contractManagement/ContractByID/ContractByIDRightPanel.tsx';
import {
  ContractByIDFormValues,
  contractByIdValidationSchema,
  getContractByIDInitialValues,
  normalizeFormattedTotalValue,
} from '@/modules/admin-dashboard/components/contractManagement/ContractByID/ContractDetailsTab.tsx';
import { ContractFilesSidebar } from '@/modules/admin-dashboard/components/contractManagement/ContractByID/ContractFilesSidebar.tsx';
import { UploadContractFilesModal } from '@/modules/admin-dashboard/components/contractManagement/ContractByID/UploadContractFilesModal.tsx';
import {
  getContractFileCategory,
  getContractFileName,
} from '@/modules/admin-dashboard/components/contractManagement/ContractByID/contractFiles.ts';
import { ContractStatusBadge } from '@/modules/admin-dashboard/components/contractManagement/ContractStatusBadge.tsx';
import {
  useContractContributorsQuery,
  useContractPartiesDetailsQuery,
  useContractQuery,
  useUpdateContractMutation,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import { PageLayout, Spinner } from 'ui';
import { ArrowLeftIcon, EditGrayIcon } from 'ui/icons';

const ContractByID = () => {
  const { id } = useParams<{ id: string }>();
  const contractId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  const { email: currentUserEmail } = useGetUserInfo();
  const [activeTab, setActiveTab] = useState<ContractInfoTab>('details');
  const [activeFileId, setActiveFileId] = useState<number>();
  const [isUploadFilesModalOpen, setIsUploadFilesModalOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { data, isFetching, error, isFetched } = useContractQuery(contractId, !contractId);
  const { data: contractPartiesData } = useContractPartiesDetailsQuery(
    contractId,
    !contractId ||
      !isFetched ||
      get(error, 'response.status') === 400 ||
      get(error, 'response.status') === 404,
  );
  const { data: contributorsData } = useContractContributorsQuery(
    { contractId },
    !contractId ||
      !isFetched ||
      get(error, 'response.status') === 400 ||
      get(error, 'response.status') === 404,
  );

  const contract = data?.contract;
  const currentContributor = useMemo(
    () =>
      (contributorsData?.contributors || []).find(contributor => {
        if (!currentUserEmail) return false;
        return contributor.email === currentUserEmail;
      }),
    [contributorsData?.contributors, currentUserEmail],
  );
  const isReadOnly = currentContributor?.permission_level === ContractPermissionLevel.Viewer;
  const documents = useMemo(() => {
    if (data?.documents?.length) return data.documents;
    return data?.file ? [data.file] : [];
  }, [data?.documents, data?.file]);
  const preferredActiveFile = useMemo(
    () =>
      documents.find(file => getContractFileCategory(file) === ContractFileCategory.CoreDocument) ||
      documents[0],
    [documents],
  );
  const activeFile = useMemo(
    () => documents.find(file => file.id === activeFileId) || preferredActiveFile,
    [activeFileId, documents, preferredActiveFile],
  );

  const { data: clientsData } = useClientsListQuery(
    {
      page: 1,
      page_size: 100,
      filter_by_name: '',
    },
    !contract?.client_id,
  );

  const clientOption = useMemo(() => {
    if (!contract?.client_id) return null;
    const client = (clientsData?.pages || [])
      .flatMap(page => page.data)
      .find(item => item.id === contract.client_id);
    return client
      ? { value: client.id, label: client.name }
      : { value: contract.client_id, label: `Client #${contract.client_id}` };
  }, [clientsData?.pages, contract?.client_id]);

  const contractNameWidthCap = useMemo(() => {
    if (viewportWidth >= 1536) return 'calc(100vw - 850px)';
    if (viewportWidth >= 1280) return 'calc(100vw - 720px)';
    if (viewportWidth >= 1024) return 'calc(100vw - 540px)';
    if (viewportWidth >= 640) return 'calc(100vw - 440px)';
    return 'calc(100vw - 180px)';
  }, [viewportWidth]);

  const contractForm = useFormik<ContractByIDFormValues>({
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: getContractByIDInitialValues(contract ? data : undefined, clientOption),
    validationSchema: contractByIdValidationSchema,
    onSubmit: async values => {
      if (!contract) return;

      await updateContractMutation({
        id: contract.id,
        data: {
          name: values.name || undefined,
          status: values.status,
          currency_code: values.currency_code,
          owner_id: values.owner_id,
          client_id: values.client_id,
          jurisdiction_id: values.jurisdiction_id,
          category: values.category || undefined,
          total_value: values.total_value
            ? Number(normalizeFormattedTotalValue(values.total_value))
            : undefined,
          value_period: values.value_period || undefined,
          execution_date: values.execution_date
            ? values.execution_date.format('YYYY-MM-DD')
            : undefined,
          expiry_date: values.expiry_date ? values.expiry_date.format('YYYY-MM-DD') : undefined,
          notice_period_days: values.notice_period_days
            ? Number(values.notice_period_days)
            : undefined,
          term_for_convenience: values.term_for_convenience,
          policy_number: values.policy_number || undefined,
          physical_storage_location: values.physical_storage_location || undefined,
          comments: values.comments || undefined,
        },
      });
      contractForm.resetForm({ values });
    },
  });

  const { mutateAsync: updateContractMutation } = useUpdateContractMutation(contractForm.setErrors);

  useEffect(() => {
    if (data?.contract) {
      contractForm.resetForm({
        values: getContractByIDInitialValues(data, clientOption),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.contract, clientOption]);

  // useEffect(() => {
  //   const status = get(error, 'response.status');
  //   if (status === 400 || status === 404) {
  //     toast.error('Contract not found', { id: 'contract-id' });
  //   }
  // }, [error]);

  useEffect(() => {
    if (!documents.length) {
      setActiveFileId(undefined);
      return;
    }

    if (!activeFileId || !documents.some(file => file.id === activeFileId)) {
      setActiveFileId(preferredActiveFile?.id);
    }
  }, [activeFileId, documents, preferredActiveFile]);

  const handleDownload = useCallback(() => {
    if (!activeFile?.url) return;
    const link = document.createElement('a');
    link.href = activeFile.url;
    link.download = getContractFileName(activeFile);
    link.target = '_blank';
    link.rel = 'noreferrer';
    link.click();
  }, [activeFile]);

  const handleGoBack = useCallback(() => {
    navigate({
      pathname: adminDashboardPaths.static.contractManagement,
      search: location.search,
    });
  }, [location.search, navigate]);

  if (get(error, 'response.status') === 400 || get(error, 'response.status') === 404) {
    return (
      <Navigate
        to={{ pathname: adminDashboardPaths.static.contractManagement, search: location.search }}
      />
    );
  }

  return (
    <FormikProvider value={contractForm}>
      <PageLayout
        title={
          <ContractByIDTitle
            status={contract?.status}
            onBack={handleGoBack}
            isLoading={isFetching && !contract}
            contractNameWidthCap={contractNameWidthCap}
            readOnly={isReadOnly}
          />
        }
        headerAction={
          <ContractByIDHeaderAction
            onDownload={handleDownload}
            onUploadFiles={() => setIsUploadFilesModalOpen(true)}
            hasFile={Boolean(activeFile?.url)}
            showUploadFiles={documents.length < 2}
            readOnly={isReadOnly}
          />
        }
        contentClassName="px-0 py-0 lg:!overflow-hidden"
        classNameTitle="[&>div:first-child]:flex-1 flex-col sm:flex-row flex-wrap"
      >
        {isFetching && !contract ? (
          <div className="flex min-h-[480px] items-center justify-center">
            <Spinner size="lg" variant="primary" />
          </div>
        ) : !contract ? (
          <div className="px-5 py-8 text-sm text-gray-500">Contract not found.</div>
        ) : (
          <div className="flex min-h-0 flex-col lg:h-full lg:flex-row">
            {documents.length > 1 && (
              <ContractFilesSidebar
                files={documents}
                contractId={contractId}
                activeFileId={activeFile?.id}
                onSelectFile={setActiveFileId}
                onUploadFiles={() => setIsUploadFilesModalOpen(true)}
                readOnly={isReadOnly}
              />
            )}
            <div className="relative z-0 min-w-0 flex-1 border-b border-gray-200 bg-gray-50 lg:border-b-0 lg:border-r">
              <div className="h-full p-4 sm:p-5">
                <ContractByIDPreviewPanel
                  fileUrl={activeFile?.url}
                  fileName={
                    activeFile?.original_file_name || activeFile?.stored_file_name || undefined
                  }
                />
              </div>
            </div>

            <ContractByIDRightPanel
              contractId={contractId}
              clientId={contract?.client_id}
              parties={contractPartiesData?.data || []}
              lifecycleEvents={data?.lifecycle_events || []}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              readOnly={isReadOnly}
            />
          </div>
        )}
        <UploadContractFilesModal
          contractId={contractId}
          open={isUploadFilesModalOpen}
          onClose={() => setIsUploadFilesModalOpen(false)}
        />
      </PageLayout>
    </FormikProvider>
  );
};

const ContractByIDTitle = ({
  status,
  onBack,
  isLoading,
  contractNameWidthCap,
  readOnly,
}: {
  status?: ContractStatus;
  onBack: () => void;
  isLoading: boolean;
  contractNameWidthCap: string;
  readOnly: boolean;
}) => {
  const formik = useFormikContext<ContractByIDFormValues>();
  const displayValue = formik.values.name || (isLoading ? 'Loading...' : 'Enter contract name...');
  const inputWidth = Math.max(displayValue.length, 8);

  return (
    <div className="flex min-w-0 items-center gap-3">
      <button
        onClick={onBack}
        className="flex h-8 w-8 items-center justify-center rounded transition-colors hover:bg-gray-100"
        aria-label="Go back"
      >
        <img src={ArrowLeftIcon} alt="Back" className="h-6 w-6" />
      </button>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <input
            name="name"
            id="name"
            value={formik.values.name}
            onChange={e => {
              formik.setFieldValue('name', e.target.value);
              formik.setFieldError('name', '');
            }}
            style={{
              width: `min(${Math.max(inputWidth - 3, 13)}ch, ${contractNameWidthCap})`,
            }}
            className="min-w-0 flex-none w-auto border-0 bg-transparent px-0 py-0 text-2xl font-semibold text-gray-900 outline-none placeholder:text-gray-400"
            placeholder={isLoading ? 'Loading...' : 'Enter contract name...'}
            disabled={isLoading || readOnly}
          />
          <button
            type="button"
            onClick={() => {
              if (readOnly) return;
              const input = document.getElementById('name');
              if (input instanceof HTMLInputElement) {
                input.focus();
              }
            }}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded transition-colors hover:bg-gray-100 disabled:opacity-50"
            aria-label="Edit contract name"
            disabled={isLoading || readOnly}
          >
            <img src={EditGrayIcon} alt="" className="h-5 w-5" />
          </button>
        </div>
        {status && <ContractStatusBadge status={status} />}
      </div>
    </div>
  );
};

export default withPrivateAccess(ContractByID, Role.COMPLIANCE_ADMIN);
